import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function Transactions() {
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const token = localStorage.getItem('token');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const [transactionsRes, accountsRes] = await Promise.all([
        axios.get(`${API_URL}/transactions`, config),
        axios.get(`${API_URL}/accounts`, config)
      ]);

      setTransactions(transactionsRes.data);
      setAccounts(accountsRes.data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredTransactions = () => {
    const accountIds = accounts.map(acc => acc.id);
    
    if (filter === 'all') return transactions;
    if (filter === 'sent') return transactions.filter(tx => accountIds.includes(tx.from_account));
    if (filter === 'received') return transactions.filter(tx => accountIds.includes(tx.to_account) && !accountIds.includes(tx.from_account));
    return transactions;
  };

  if (loading) {
    return <div className="text-center py-20">Chargement...</div>;
  }

  const filteredTransactions = getFilteredTransactions();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Mes Transactions</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Toutes
          </button>
          <button
            onClick={() => setFilter('sent')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'sent' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Envoyées
          </button>
          <button
            onClick={() => setFilter('received')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'received' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Reçues
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <p className="text-sm text-gray-600">Total transactions</p>
          <p className="text-2xl font-bold text-blue-600">{transactions.length}</p>
        </div>
        <div className="bg-red-50 rounded-lg p-4 border border-red-200">
          <p className="text-sm text-gray-600">Envoyées</p>
          <p className="text-2xl font-bold text-red-600">
            {transactions.filter(tx => accounts.map(a => a.id).includes(tx.from_account)).length}
          </p>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <p className="text-sm text-gray-600">Reçues</p>
          <p className="text-2xl font-bold text-green-600">
            {transactions.filter(tx => accounts.map(a => a.id).includes(tx.to_account) && !accounts.map(a => a.id).includes(tx.from_account)).length}
          </p>
        </div>
      </div>

      {/* Transactions List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            Historique complet ({filteredTransactions.length})
          </h2>
          <div className="space-y-3">
            {filteredTransactions.length === 0 ? (
              <p className="text-gray-500 text-center py-12">Aucune transaction</p>
            ) : (
              filteredTransactions.map((tx) => {
                const accountIds = accounts.map(a => a.id);
                const isSent = accountIds.includes(tx.from_account);
                const isReceived = accountIds.includes(tx.to_account);

                return (
                  <div
                    key={tx.id}
                    className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-2xl">
                            {isSent && !isReceived ? '📤' : '📥'}
                          </span>
                          <p className="font-semibold text-gray-900">{tx.description}</p>
                        </div>
                        <div className="space-y-1 text-sm text-gray-600">
                          <p>
                            <span className="font-medium">Type:</span> {tx.transaction_type}
                          </p>
                          <p>
                            <span className="font-medium">Date:</span>{' '}
                            {new Date(tx.created_at).toLocaleString('fr-FR', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                          {tx.from_account && (
                            <p className="font-mono text-xs">
                              <span className="font-medium">De:</span> {tx.from_account.substring(0, 8)}...
                            </p>
                          )}
                          {tx.to_account && (
                            <p className="font-mono text-xs">
                              <span className="font-medium">Vers:</span> {tx.to_account.substring(0, 8)}...
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        <p className={`text-2xl font-bold ${
                          isSent && !isReceived ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {isSent && !isReceived ? '-' : '+'}
                          {tx.amount?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                        </p>
                        <span className={`text-xs px-2 py-1 rounded-full inline-block mt-1 ${
                          tx.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {tx.status}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Transactions;
